import { ArrayUtil, getThemeForKey, StringUtil } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { Button } from "../button/Button";
import { ButtonVariant } from "../button/ButtonVariant";
import { ClickEvent } from "../../events/ClickEvent";
import { Component } from "../..";
import { ComponentStyles } from "../../base/ComponentStyles";
import { Image } from "../image/Image";
import { Label } from "../label/Label";
import { LabelSizer } from "../label/LabelSizer";
import { Panel } from "../panel/Panel";
import { PanelProps } from "../panel/PanelProps";
import { Tab } from "../tabset/Tab";
import { Tabset } from "./Tabset";

enum InnerIds {
    CLOSE_BUTTON = "closeButton",
    IMAGE = "headingImage",
    LABEL = "headingLabel"
}

export abstract class TabSelectorPanel extends Panel {
    private static readonly commonThemeObject = getThemeForKey("tabset.headingCommon") ?? {};
    private static readonly commonLabelThemeObject = getThemeForKey("tabset.headingCommonLabel") ?? {};
    private closeButton: Button;
    private _image: Image;
    private _label: Label;
    syncWidthListenerActive = false;
    protected tab: Tab;

    constructor(tab: Tab, props?: Partial<PanelProps>) {
        super(props, false);
        this._shouldAddDesignerContainerProperties = false;
        this.allowClicksInDesigner = true;
        this.allowDropInDesigner = false;
        this.tab = tab;
        this.setProps({
            ...this.commonProps,
            ...props
        });
        this.syncLabel();
        this.syncImage();
        this.syncCloseable();
        this.syncStyle();
        this.sizeLabel();
        this.addContentsChangedListener(() => this.syncWidth());
        this.syncWidthListenerActive = true;
    }

    private get tabset(): Tabset {
        return this.tab.tabset;
    }

    override syncInnerComponentIds() {
        if (this.closeButton != null) {
            this.closeButton.id = this.tab.createInnerComponentId(InnerIds.CLOSE_BUTTON);
        }
        if (this.image != null) {
            this.image.id = this.tab.createInnerComponentId(InnerIds.IMAGE);
        }
        if (this.label != null) {
            this.label.id = this.tab.createInnerComponentId(InnerIds.LABEL);
        }
    }

    private get commonProps(): Partial<PanelProps> {
        return {
            ...TabSelectorPanel.commonThemeObject,
            _designer: this.tab._designer,
            id: this.tab.createInnerComponentId("tabHeading"),
            maxWidth: this.tab.headingMaxWidth,
            minWidth: this.tab.isVertical() ? null : this.tab.headingMinWidth,
            rowBreak: false,
            visible: this.tab.visible
        }
    }

    syncWidth(refreshUnderline: boolean = true) {
        if (this.syncWidthListenerActive !== true) {
            return;
        }
        this.syncWidthListenerActive = false;
        try {
            if (this.tab.isVertical()) {
                // Set all tabs' width to the width of the longest tab, limited by the min/max width
                this.tabset.syncTabSelectorWidth();
            } else {
                // Set the tab's min/max width based on the provided properties
                this.maxWidth = this.tab.headingMaxWidth;
                this.minWidth = this.tab.headingMinWidth;
                this.sizeLabel();
            }
        } finally {
            this.syncWidthListenerActive = true;
        }
        if (refreshUnderline) {
            this.tabset?.refreshUnderline();
        }
    }

    public get label(): Label {
        return this._label;
    }

    private set label(value: Label) {
        this._label = value;
    }

    syncLabel() {
        try {
            if (StringUtil.isEmptyString(this.tab.caption)) {
                if (this.label != null) {
                    this.remove(this.label);
                    this.label = null;
                }
                return;
            }
            if (this.label == null) {
                const paddingTopBottom = this.tab.isVertical() ? 0 : 4;
                this.label = new Label({
                    ...TabSelectorPanel.commonLabelThemeObject,
                    _designer: this._designer,
                    id: this.tab.createInnerComponentId(InnerIds.LABEL),
                    paddingBottom: paddingTopBottom,
                    paddingTop: paddingTopBottom,
                    rowBreak: false,
                    text: this.tab.caption,
                    tooltip: this.tooltip,
                    tooltipDynamicContent: this.tooltipDynamicContent
                });

                // If in the designer, we want no pointer events so that the user can click on tab headings.
                // If not in the designer, we want pointer events so that the tab's tooltip can be displayed.
                if (this._designer != null) {
                    this.label.style.pointerEvents = "none";
                }

                const insertionIndex = this.image != null && this.contains(this.image) ? this.indexOf(this.image) + 1 : 0;
                this.insert(this.label, insertionIndex);
                this.syncImageMargin();
            } else {
                this.label.text = this.tab.caption;
            }
        } finally {
            this.tabset?.refreshUnderline();
        }
    }

    sizeLabel() {
        new LabelSizer(this, this.label).sizeForMaxWidth();
    }

    get image(): Image {
        return this._image;
    }

    private set image(value: Image) {
        this._image = value;
    }

    syncImage() {
        try {
            if (this.tab.imageName == null) {
                if (this.image != null) {
                    this.remove(this.image);
                    this.image = null;
                }
                return;
            }
            let createdImage = false;
            if (this.image == null) {
                this.image = new Image({
                    id: this.tab.createInnerComponentId(InnerIds.IMAGE),
                    rowBreak: false
                });
                createdImage = true;
            }
            this.applyImagePropsToHeading();
            this.syncImageMargin();
            if (createdImage) {
                if (this.label != null && this.contains(this.label)) {
                    this.insert(this.image, this.indexOf(this.label));
                } else {
                    this.insert(this.image, 0);
                }
            }
        } finally {
            this.tabset?.refreshUnderline();
        }
    }

    private syncImageMargin() {
        if (this.image != null) {
            this.image.marginRight = this.getComponentCount() > 0 ? 6 : 0;
        }
    }

    private applyImagePropsToHeading() {
        this.image.setProps({
            color: this.tab.imageColor,
            fill: this.tab.imageFill,
            height: this.tab.imageHeight,
            name: this.tab.imageName,
            rotation: this.tab.imageRotation,
            stroke: this.tab.imageStroke,
            strokeWidth: this.tab.imageStrokeWidth,
            width: this.tab.imageWidth
        });
    }

    syncCloseable() {
        if (this.tab.closeable === true) {
            this.closeButton = new Button({
                id: this.tab.createInnerComponentId(InnerIds.CLOSE_BUTTON),
                imageName: ImageName.close,
                variant: ButtonVariant.round,
                focusable: false,
                marginTop: 2,
                imageWidth: 14,
                imageHeight: 14,
                padding: 0,
                margin: 0,
                marginLeft: 6
            });
            this.closeButton.addClickListener((event: ClickEvent) => this.tab.close(event));
            this.add(this.closeButton);
        }
        else {
            this.remove(this.closeButton);
            this.closeButton = undefined;
        }
        this.tabset?.refreshUnderline();
    }

    syncStyle() {
        const tabIsSelected = this.tab.selected;
        this.applyHoverClass();
        const themeObject = tabIsSelected ? this.selectedThemeObject : this.unselectedThemeObject;
        if (themeObject != null) {
            this.applyTheme(themeObject, true);
        }
    }

    protected applyHoverClass() {
        const tabIsSelected = this.tab.selected;
        this.setClassIncluded(ComponentStyles.hoverShade, !tabIsSelected);
    }

    protected get selectedThemeObject(): any {
        return null;
    }

    protected get unselectedThemeObject(): any {
        return null;
    }

    protected override doAfterChildAdded(child: Component) {
        super.doAfterChildAdded(child);
        if (child instanceof Image) {
            child.setProps({ height: 21, width: 21 });
        }
    }

    get adhocComponents(): Component[] {
        const providedComponents = this.providedComponents;
        return this.components.filter(component => !providedComponents.includes(component));
    }

    get providedComponents(): Component[] {
        return [ this.image, this.label, this.closeButton ].filter(component => component != null);
    }

    get insertComponentsBeforeThis(): Component {
        return null;
    }
}
